import { useEffect, useState } from 'react';
import { OTPInput, REGEXP_ONLY_DIGITS } from 'input-otp';
import { PrimaryButton } from './PrimaryButton';

type Props = {
  value: string;
  onChange: (otp: string) => void;
  onVerify: () => void;
  onResend: () => void;
  submitting: boolean;
  /** Drives the red error state on the boxes (message is shown via toast). */
  error?: boolean;
  /** Seconds the user must wait before "Resend code" becomes available. */
  resendSeconds?: number;
};

const formatCountdown = (s: number): string => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

/**
 * Six-box OTP entry used by the password-recovery flows. Encapsulates
 * the boxed input plus the resend cooldown: "Resend code" stays locked
 * behind a countdown (default 60s) and re-locks each time it's used, so
 * the user can't hammer the send-OTP endpoint.
 */
export function OtpVerify({
  value,
  onChange,
  onVerify,
  onResend,
  submitting,
  error = false,
  resendSeconds = 60,
}: Props) {
  const [secondsLeft, setSecondsLeft] = useState(resendSeconds);

  // Tick down once a second until the resend window opens. A fresh
  // timeout per tick keeps the effect self-cleaning and stops at zero.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  const handleResend = () => {
    if (secondsLeft > 0 || submitting) return;
    setSecondsLeft(resendSeconds); // re-lock the cooldown
    onResend();
  };

  return (
    <>
      <OTPInput
        maxLength={6}
        value={value}
        onChange={onChange}
        pattern={REGEXP_ONLY_DIGITS}
        inputMode="numeric"
        autoFocus
        containerClassName="w-full"
        render={({ slots }) => (
          <div className="flex w-full justify-center gap-2">
            {slots.map((slot, i) => (
              <Slot
                key={i}
                char={slot.char}
                isActive={slot.isActive}
                hasFakeCaret={slot.hasFakeCaret}
                error={error}
              />
            ))}
          </div>
        )}
      />

      <PrimaryButton
        onClick={onVerify}
        className="w-full"
        disabled={submitting || value.length !== 6}
      >
        {submitting ? 'Verifying…' : 'Verify OTP'}
      </PrimaryButton>

      {secondsLeft > 0 ? (
        <p className="text-center font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px]">
          Resend code in{' '}
          <span className="font-bold text-[#3a3335]">
            {formatCountdown(secondsLeft)}
          </span>
        </p>
      ) : (
        <button
          type="button"
          onClick={handleResend}
          disabled={submitting}
          className="font-['DM_Sans',sans-serif] text-[#8e2a2f] text-[13px] font-bold hover:underline disabled:opacity-50"
        >
          Resend code
        </button>
      )}
    </>
  );
}

function Slot({
  char,
  isActive,
  hasFakeCaret,
  error,
}: {
  char: string | null;
  isActive: boolean;
  hasFakeCaret: boolean;
  error: boolean;
}) {
  return (
    <div
      className={`relative flex h-[56px] flex-1 items-center justify-center rounded-[10px] border bg-white text-[20px] font-bold text-[#3a3335] transition-all ${
        isActive
          ? 'border-[#8e2a2f] ring-2 ring-[#8e2a2f]/20'
          : error
            ? 'border-red-400'
            : 'border-[#eee]'
      }`}
    >
      {char}
      {hasFakeCaret && !char ? (
        <div className="pointer-events-none absolute h-6 w-px animate-pulse bg-[#3a3335]" />
      ) : null}
    </div>
  );
}
